import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { getErrorMsg } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Zap, Activity, Gauge, Download, RefreshCw,
  Clock, Plug, TrendingUp, CalendarDays, ChevronLeft, ChevronRight,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Area, AreaChart,
} from "recharts";

function MetricCard({ icon: Icon, label, value, unit, color }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <p className="text-xl font-bold text-gray-900 font-mono">
        {value != null ? value : "–"}
        {value != null && unit && <span className="text-sm font-normal text-gray-400 ml-1">{unit}</span>}
      </p>
    </div>
  );
}

function toLocalDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Bucket telemetry history into N-minute slots for chart display.
 * Liefert genau einen Datenpunkt pro Bucket (den LETZTEN Eintrag im Bucket - so
 * matched es das Messkoffer-Verhalten). CSV-Export nutzt diese Funktion NICHT
 * und behaelt volle Aufloesung.
 */
function bucketHistory(history, bucketMinutes = 5) {
  if (!history || history.length === 0) return [];
  const bucketMs = bucketMinutes * 60 * 1000;
  const buckets = new Map();
  for (const h of history) {
    if (!h.ts_utc) continue;
    const t = new Date(h.ts_utc).getTime();
    const key = Math.floor(t / bucketMs) * bucketMs;
    // Letzter Wert im Bucket gewinnt (Map-Set ueberschreibt)
    buckets.set(key, h);
  }
  // Sortiert nach Bucket-Start-Zeit
  return Array.from(buckets.entries())
    .sort(([a], [b]) => a - b)
    .map(([, h]) => h);
}

export default function KirmesZaehlerPage() {
  const { eventId, signupId } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [signup, setSignup] = useState(null);
  const [meterData, setMeterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Day-based navigation: defaults to today, wird in useEffect nach Event-Load
  // auf den Dispo-Zeitraum geklemmt (siehe weiter unten).
  const [selectedDate, setSelectedDate] = useState(toLocalDateStr(new Date()));

  const loadData = useCallback(async () => {
    try {
      // Load only the single signup (fast) — event name is optional/cosmetic
      const signupRes = await api.get(`/kirmes/signups/${signupId}`);
      setSignup(signupRes.data);
      // Load event name in background (non-blocking)
      api.get(`/kirmes/events/${eventId}`).then(r => setEvent(r.data)).catch(() => {});
    } catch {
      toast.error("Fehler beim Laden");
      navigate(`/kirmes/${eventId}`);
    } finally {
      setLoading(false);
    }
  }, [eventId, signupId, navigate]);

  const loadMeterData = useCallback(async () => {
    if (!signupId || !selectedDate) return;
    try {
      const params = { limit: 90000 };
      params.from_time = new Date(selectedDate + "T00:00:00").toISOString();
      params.to_time = new Date(selectedDate + "T23:59:59").toISOString();
      const r = await api.get(`/kirmes/signups/${signupId}/meter-data`, { params });
      setMeterData(r.data);
    } catch (err) {
      // Bei Fehler: meterData unverändert lassen damit "Kein Zähler verknüpft"
      // nicht fälschlich angezeigt wird. Nur einmal pro Tag-Wechsel toasten.
      // eslint-disable-next-line no-console
      console.warn("Telemetrie-Abruf fehlgeschlagen:", err?.response?.status, err?.message);
    }
  }, [signupId, selectedDate]);

  useEffect(() => { loadData(); loadMeterData(); }, [loadData, loadMeterData]);

  // Wenn das Event geladen wird: selectedDate auf den Dispo-Zeitraum clampen.
  // Damit zeigt die Auswertung nach Event-Ende automatisch den LETZTEN Daten-
  // Tag (= dispo_end), statt "heute" (wo der Zaehler ggf. auf anderer Kirmes
  // laeuft -> dort sind keine Daten zugeordnet).
  const minDate = event?.dispo_start || event?.start_date || null;
  const maxDate = event?.dispo_end || event?.end_date || null;
  useEffect(() => {
    // NUR bei Event-Load laufen (minDate/maxDate-Change), NICHT bei selectedDate-Change.
    // Funktionales Update: wenn der State passt, wird dieselbe Referenz zurueckgegeben
    // -> kein Re-Render, kein Loop.
    if (!maxDate) return;
    const today = toLocalDateStr(new Date());
    setSelectedDate(prev => {
      if (today > maxDate && prev > maxDate) return maxDate;
      if (minDate && prev < minDate) return minDate;
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxDate, minDate]);

  // Auto-refresh every 30s
  useEffect(() => {
    const iv = setInterval(loadMeterData, 30000);
    return () => clearInterval(iv);
  }, [loadMeterData]);

  // Day navigation helpers (mit Dispo-Zeitraum-Clamping)
  const goDay = (offset) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + offset);
    const next = toLocalDateStr(d);
    if (maxDate && next > maxDate) return; // nicht ueber Ausbau hinaus
    if (minDate && next < minDate) return; // nicht vor Aufbau zurueck
    setSelectedDate(next);
  };
  const todayStr = toLocalDateStr(new Date());
  const effectiveToday = maxDate && todayStr > maxDate ? maxDate : todayStr;
  const isAtEffectiveToday = selectedDate === effectiveToday;
  const canGoNext = !maxDate || selectedDate < maxDate;
  const canGoPrev = !minDate || selectedDate > minDate;
  const displayDate = (() => {
    try {
      const d = new Date(selectedDate);
      if (Number.isNaN(d.getTime())) return selectedDate || "-";
      return d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
    } catch { return selectedDate || "-"; }
  })();

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = { limit: 999999 };
      params.from_time = new Date(selectedDate + "T00:00:00").toISOString();
      params.to_time = new Date(selectedDate + "T23:59:59").toISOString();
      const r = await api.get(`/kirmes/signups/${signupId}/meter-data`, { params });
      const history = r.data?.history || [];
      if (history.length === 0) { toast.error("Keine Daten zum Exportieren"); return; }

      const cols = ["ts_local", "ts_utc", "P_sum_kW", "P_L1_kW", "P_L2_kW", "P_L3_kW", "I_L1", "I_L2", "I_L3", "I_sum", "U_L1", "U_L2", "U_L3", "F_Hz", "E_imp_kWh"];
      const headers = ["Zeit (Europe/Berlin)", "Zeit (UTC)", "Leistung ges. (kW)", "Leistung L1 (kW)", "Leistung L2 (kW)", "Leistung L3 (kW)", "Strom L1 (A)", "Strom L2 (A)", "Strom L3 (A)", "Strom ges. (A)", "Spannung L1 (V)", "Spannung L2 (V)", "Spannung L3 (V)", "Frequenz (Hz)", "Energie (kWh)"];
      // Berlin-Lokal-Zeitstempel (mit DST-Auto-Umstellung)
      const localFmt = new Intl.DateTimeFormat("de-DE", {
        timeZone: "Europe/Berlin",
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
      });
      const toLocal = (utcIso) => {
        if (!utcIso) return "";
        const d = new Date(utcIso);
        if (Number.isNaN(d.getTime())) return utcIso;
        const parts = Object.fromEntries(
          localFmt.formatToParts(d).filter(p => p.type !== "literal").map(p => [p.type, p.value])
        );
        const ms = String(d.getUTCMilliseconds()).padStart(3, "0");
        return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second},${ms}`;
      };
      // Zahlen mit Komma als Dezimaltrenner ausgeben (DE-Format), sonst
      // interpretiert Excel z.B. "1.4" als Datum "1. April".
      const formatCell = (v) => {
        if (v == null) return "";
        if (typeof v === "number") return String(v).replace(".", ",");
        const s = String(v);
        if (/^-?\d+\.\d+$/.test(s)) return s.replace(".", ",");
        return s;
      };
      let csv = headers.join(";") + "\n";
      for (const row of history) {
        const rowWithLocal = { ...row, ts_local: toLocal(row.ts_utc) };
        csv += cols.map(c => formatCell(rowWithLocal[c])).join(";") + "\n";
      }

      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const meterName = signup?.emu_meter_name || "Zaehler";
      a.download = `Zaehlerdaten_${meterName}_${selectedDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${history.length} Datenpunkte exportiert`);
    } catch (err) {
      toast.error(getErrorMsg(err, "Export fehlgeschlagen"));
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-400">Laden...</div>;

  const latest = meterData?.latest;
  const history = meterData?.history || [];
  const isOnline = meterData?.is_online;
  // WICHTIG: Verknüpfung aus dem Signup-Objekt selbst ableiten (matcht
  // EventDetailPage). Wenn wir uns auf meterData.linked verlassen würden,
  // zeigt die Seite fälschlich "Kein Zähler verknüpft" wenn der
  // Telemetrie-Endpoint (z.B. wegen Timeout oder fehlender Daten am
  // gewählten Tag) noch nicht zurück ist.
  const linked = !!(signup?.emu_device_id && signup?.emu_meter_id) || !!meterData?.linked;

  // Chart-Daten: Auf 5-Min-Buckets reduzieren (matched Messkoffer-Verhalten).
  // CSV-Export nutzt history direkt mit voller Aufloesung - siehe handleExportCSV.
  const chartHistory = bucketHistory(history, 5);
  const chartData = chartHistory.map(h => ({
    timestamp: h.ts_utc ? new Date(h.ts_utc).getTime() : 0,
    time: h.ts_utc ? new Date(h.ts_utc).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "",
    leistung: h.P_sum_kW != null ? parseFloat(h.P_sum_kW.toFixed(3)) : null,
    P_L1: h.P_L1_kW != null ? parseFloat(h.P_L1_kW.toFixed(3)) : null,
    P_L2: h.P_L2_kW != null ? parseFloat(h.P_L2_kW.toFixed(3)) : null,
    P_L3: h.P_L3_kW != null ? parseFloat(h.P_L3_kW.toFixed(3)) : null,
    I_L1: h.I_L1 != null ? parseFloat(h.I_L1.toFixed(2)) : null,
    I_L2: h.I_L2 != null ? parseFloat(h.I_L2.toFixed(2)) : null,
    I_L3: h.I_L3 != null ? parseFloat(h.I_L3.toFixed(2)) : null,
    U_L1: h.U_L1 != null ? parseFloat(h.U_L1.toFixed(1)) : null,
    U_L2: h.U_L2 != null ? parseFloat(h.U_L2.toFixed(1)) : null,
    U_L3: h.U_L3 != null ? parseFloat(h.U_L3.toFixed(1)) : null,
    energie: h.E_imp_kWh != null ? parseFloat(h.E_imp_kWh.toFixed(2)) : null,
    frequenz: h.F_Hz != null ? parseFloat(h.F_Hz.toFixed(1)) : null,
  }));

  // X-axis domain: 00:00 - 24:00 for selected day
  const dayStart = new Date(selectedDate + "T00:00:00").getTime();
  const dayEnd = new Date(selectedDate + "T23:59:59").getTime();
  const xTicks = Array.from({ length: 13 }, (_, i) => dayStart + i * 2 * 3600000); // every 2h
  const formatXTick = (ts) => {
    const d = new Date(ts);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  // Metriken: letzter Wert des gewaehlten Tages (nicht absolut letzter)
  const dayLatest = history.length > 0 ? history[history.length - 1] : latest;

  const schausteller = signup?.schausteller;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="zaehler-page">
      <header className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/kirmes/${eventId}`)} className="text-gray-600 hover:text-fuchsia-600 flex-shrink-0" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Zurück</span>
            </Button>
            <div className="h-5 w-px bg-gray-200 hidden sm:block" />
            <div className="min-w-0">
              <h1 className="text-sm sm:text-base font-semibold text-gray-900">Zählerdaten</h1>
              <p className="text-[10px] sm:text-xs text-gray-500 truncate">
                {signup?.emu_meter_name || "–"} · {schausteller?.name || "–"} · {signup?.fahrgeschaeft || "–"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isOnline != null && (
              <span className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-full ${isOnline ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                {isOnline ? "Online" : "Offline"}
              </span>
            )}
            <Button size="sm" variant="outline" onClick={loadMeterData} className="text-xs" data-testid="refresh-btn">
              <RefreshCw className="w-3 h-3 sm:mr-1" /> <span className="hidden sm:inline">Aktualisieren</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Day Navigation */}
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="day-navigation">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2 min-w-0">
              <CalendarDays className="w-4 h-4 text-fuchsia-500 flex-shrink-0" />
              <span className="text-sm font-semibold text-gray-900 truncate">{event?.name}</span>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 sm:ml-auto">
              <Button variant="outline" size="sm" onClick={() => goDay(-1)}
                disabled={!canGoPrev}
                className="h-8 w-8 p-0" data-testid="day-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <button
                onClick={() => setSelectedDate(effectiveToday)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors min-w-[180px] text-center ${isAtEffectiveToday ? "bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200" : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"}`}
                title={maxDate && todayStr > maxDate ? `Event endete ${maxDate} - Sprung zum letzten Datentag` : "Heute"}
                data-testid="day-display"
              >
                {displayDate}
              </button>
              <Button variant="outline" size="sm" onClick={() => goDay(1)}
                disabled={!canGoNext}
                className="h-8 w-8 p-0" data-testid="day-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <Button size="sm" onClick={handleExportCSV} disabled={exporting || !linked}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs w-full sm:w-auto" data-testid="export-csv-btn">
              <Download className="w-3 h-3 mr-1" /> {exporting ? "Exportiert..." : "CSV Export"}
            </Button>
          </div>
        </div>

        {!linked ? (
          <div className="text-center py-12 text-gray-400">
            <Plug className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">Kein Zähler verknüpft</p>
            <p className="text-xs mt-1">Verknüpfen Sie einen EMU-Zähler auf der Event-Detailseite</p>
          </div>
        ) : (
          <>
            {/* Live Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="live-metrics">
              <MetricCard icon={Zap} label="Leistung" value={dayLatest?.P_sum_kW?.toFixed(3)} unit="kW" color="bg-fuchsia-500" />
              <MetricCard icon={TrendingUp} label="Energie" value={dayLatest?.E_imp_kWh?.toFixed(2)} unit="kWh" color="bg-emerald-500" />
              <MetricCard icon={Clock} label="Frequenz" value={dayLatest?.F_Hz?.toFixed(1)} unit="Hz" color="bg-purple-500" />
              {/* Spannung pro Phase */}
              <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="metric-spannung">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
                    <Gauge className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-xs text-gray-500">Spannung</span>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">L1</span>
                    <span className="font-bold font-mono text-gray-900">{dayLatest?.U_L1 != null ? `${dayLatest.U_L1.toFixed(1)} V` : "–"}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">L2</span>
                    <span className="font-bold font-mono text-gray-900">{dayLatest?.U_L2 != null ? `${dayLatest.U_L2.toFixed(1)} V` : "–"}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">L3</span>
                    <span className="font-bold font-mono text-gray-900">{dayLatest?.U_L3 != null ? `${dayLatest.U_L3.toFixed(1)} V` : "–"}</span>
                  </div>
                </div>
              </div>
              {/* Strom pro Phase */}
              <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="metric-strom">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                    <Activity className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-xs text-gray-500">Strom</span>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">L1</span>
                    <span className="font-bold font-mono text-gray-900">{dayLatest?.I_L1 != null ? `${dayLatest.I_L1.toFixed(2)} A` : "–"}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">L2</span>
                    <span className="font-bold font-mono text-gray-900">{dayLatest?.I_L2 != null ? `${dayLatest.I_L2.toFixed(2)} A` : "–"}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">L3</span>
                    <span className="font-bold font-mono text-gray-900">{dayLatest?.I_L3 != null ? `${dayLatest.I_L3.toFixed(2)} A` : "–"}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts */}
            {chartData.length > 0 ? (
              <div className="space-y-4">
                {/* Power Chart */}
                <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="chart-power">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-fuchsia-500" /> Leistungsverlauf (kW)
                  </h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="timestamp" type="number" domain={[dayStart, dayEnd]}
                        ticks={xTicks} tickFormatter={formatXTick} tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip labelFormatter={(ts) => formatXTick(ts)} contentStyle={{ fontSize: 12 }} />
                      <Area type="monotone" dataKey="P_L1" name="L1" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} strokeWidth={1.5} dot={false} />
                      <Area type="monotone" dataKey="P_L2" name="L2" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} strokeWidth={1.5} dot={false} />
                      <Area type="monotone" dataKey="P_L3" name="L3" stroke="#10b981" fill="#10b981" fillOpacity={0.1} strokeWidth={1.5} dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Current Chart - per Phase */}
                <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="chart-current">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-blue-500" /> Stromverlauf (A)
                  </h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="timestamp" type="number" domain={[dayStart, dayEnd]}
                        ticks={xTicks} tickFormatter={formatXTick} tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip labelFormatter={(ts) => formatXTick(ts)} contentStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="I_L1" name="L1" stroke="#f59e0b" strokeWidth={1.5} dot={false} />
                      <Line type="monotone" dataKey="I_L2" name="L2" stroke="#3b82f6" strokeWidth={1.5} dot={false} />
                      <Line type="monotone" dataKey="I_L3" name="L3" stroke="#10b981" strokeWidth={1.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Energy Chart */}
                <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="chart-energy">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-500" /> Energieverbrauch (kWh)
                  </h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="timestamp" type="number" domain={[dayStart, dayEnd]}
                        ticks={xTicks} tickFormatter={formatXTick} tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip labelFormatter={(ts) => formatXTick(ts)} contentStyle={{ fontSize: 12 }} />
                      <Area type="monotone" dataKey="energie" stroke="#10b981" fill="#10b981" fillOpacity={0.1} name="kWh" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400">
                <Activity className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Keine Messdaten im gewählten Zeitraum</p>
                <p className="text-xs mt-1">Passen Sie den Zeitraum an oder warten Sie auf neue Daten</p>
              </div>
            )}

            {/* Data Summary */}
            {history.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="data-summary">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Zusammenfassung</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                  <div>
                    <span className="text-gray-400">Datenpunkte</span>
                    <p className="font-mono font-semibold">{history.length}</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Max. Leistung</span>
                    <p className="font-mono font-semibold">{Math.max(...history.filter(h => h.P_sum_kW != null).map(h => h.P_sum_kW)).toFixed(3)} kW</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Max. Strom L1</span>
                    <p className="font-mono font-semibold">{Math.max(...history.filter(h => h.I_L1 != null).map(h => h.I_L1)).toFixed(2)} A</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Max. Strom L2</span>
                    <p className="font-mono font-semibold">{Math.max(...history.filter(h => h.I_L2 != null).map(h => h.I_L2)).toFixed(2)} A</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Zeitraum</span>
                    <p className="font-mono font-semibold">{displayDate}</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
